// src/components/dialogs/ClaimDetailsDialog.jsx
import React, { useMemo, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Grid,
  Avatar,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";

import { formatDateTime, moneyINR } from "../../../utils/helpers";
import { BASE_URL } from "../../config/api";

/* --------------------------------------------
   STATUS FLOW
--------------------------------------------- */
const CLAIM_STATUS_TEMPLATE = [
  {
    key: "raised",
    label: "Claim Raised",
    description: "Your claim has been submitted successfully.",
  },
  {
    key: "otp_sent",
    label: "OTP Verified",
    description: "OTP verification completed.",
  },
  {
    key: "estimate_sent",
    label: "Estimate Sent",
    description: "Inspection completed. Estimate shared.",
  },
  {
    key: "completed",
    label: "Completed",
    description: "Claim completed successfully.",
  },
  {
    key: "rejected",
    label: "Rejected",
    description: "Claim has been rejected.",
  },
];

const AppCard = ({ title, children }) => (
  <Card
    sx={{
      borderRadius: 3,
      boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
    }}
  >
    <CardContent>
      {title && (
        <Typography fontWeight={900} mb={1}>
          {title}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const Row = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography
      variant="body2"
      color="text.secondary"
      fontWeight={bold ? 700 : 400}
    >
      {label}
    </Typography>
    <Typography fontWeight={bold ? 900 : 400}>
      {value}
    </Typography>
  </Stack>
);

export default function ClaimDetailsDialog({ open, onClose, claim }) {
  const [tab, setTab] = useState(0);
  const [approving, setApproving] = useState(false);

  
  /* ---------------- PARSE INSPECTION REPORT ---------------- */
  const inspectionReport = useMemo(() => {
    try {
      return JSON.parse(claim.inspection_report || "[]");
    } catch {
      return [];
    }
  }, [claim]);

  /* ---------------- APPROVE ESTIMATE ---------------- */
  const approveEstimate = async () => {
    try {
      setApproving(true);
      await axios.post(
        `${BASE_URL}warranty/claim/estimate-approve`,
        { claim_id: claim.id }
      );
      toast.success("Estimate approved");
      onClose();
    } catch {
      toast.error("Failed to approve estimate");
    } finally {
      setApproving(false);
    }
  };


  if (!claim) return null;

  const currentIndex = CLAIM_STATUS_TEMPLATE.findIndex(
    (s) => s.key === claim.status
  );

  const employee = claim.assignment?.employee;
  

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: "8px",
          width: "calc(100% - 16px)",
        },
      }}
    >
      <Box p={2.5}>
        {/* ================= HEADER ================= */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography fontWeight={900} fontSize={18}>
              Claim Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Claim ID: {claim.id}
            </Typography>
          </Box>
          <Button size="small" onClick={onClose}>
            Close
          </Button>
        </Stack>

        {/* ================= TABS ================= */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mt: 2 }}
        >
          <Tab label="Details" />
          <Tab label="Timeline" />
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* =================================================
            TAB 1 – DETAILS (APP STYLE)
        ================================================= */}
        {tab === 0 && (
          <Stack spacing={2}>
            {/* DEVICE */}
            <AppCard title="Device">
              <Row label="Model" value={claim.device?.model || "—"} />
              <Row label="IMEI" value={claim.device?.imei1 || "—"} />
            </AppCard>

            {/* CUSTOMER */}
            <AppCard title="Customer">
              <Row label="Name" value={claim.customer?.name} />
              <Row label="Mobile" value={claim.customer?.mobile} />
              <Row label="Email" value={claim.customer?.email} />
            </AppCard>

            {/* CLAIM INFO */}
            <AppCard title="Claim Information">
              <Row label="Issue" value={claim.issue_description} />
              <Row label="Type" value={claim.claim_type} />
              <Row
                label="Created"
                value={formatDateTime(claim.created_at)}
              />
              {claim.inspection_remark && (
                <Row
                  label="Inspection Remark"
                  value={claim.inspection_remark}
                />
              )}
            </AppCard>

            {/* SERVICE DETAILS */}
            {claim.claim_type === "pickup" && claim.pickup_address && (
              <AppCard title="Pickup Address">
                <Row label="Name" value={claim.pickup_address.name} />
                <Row label="Mobile" value={claim.pickup_address.mobile} />
                <Typography variant="body2" mt={1}>
                  {claim.pickup_address.address1},{" "}
                  {claim.pickup_address.address2}
                </Typography>
                <Typography variant="body2">
                  {claim.pickup_address.city},{" "}
                  {claim.pickup_address.state} -{" "}
                  {claim.pickup_address.pincode}
                </Typography>
              </AppCard>
            )}

            {claim.claim_type === "drop" && claim.drop_retailer && (
              <AppCard title="Drop Retailer">
                <Row label="Name" value={claim.drop_retailer.business_name} />
                <Row label="City" value={claim.drop_retailer.city} />
                <Row label="Pincode" value={claim.drop_retailer.pincode} />
              </AppCard>
            )}

            {/* ASSIGNED EMPLOYEE */}
            {employee && (
              <AppCard title="Assigned Executive">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={employee.photo_url} />
                  <Box>
                    <Typography fontWeight={700}>
                      {employee.first_name} {employee.last_name}
                    </Typography>
                    <Typography variant="body2">
                      {employee.official_phone}
                    </Typography>
                    <Typography variant="body2">
                      {employee.official_email}
                    </Typography>
                  </Box>
                </Stack>
              </AppCard>
            )}

            {/* PAYMENT SUMMARY */}
            <AppCard title="Payment Summary">
              <Stack spacing={1}>
                {inspectionReport.map((item, idx) => (
                  <Row
                    key={idx}
                    label={item.label}
                    value={moneyINR(item.amount)}
                  />
                ))}
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Row
                label="Total"
                value={moneyINR(claim.payable_amount)}
                bold
              />
            </AppCard>

            {/* PHOTOS */}
            <AppCard title="Uploaded Photos">
              {claim.photos?.length ? (
                <Grid container spacing={1}>
                  {claim.photos.map((photo) => {
                    const imageUrl = `https://zoho.goelectronix.in/storage/${photo.photo_path}`;

                    return (
                      <Grid item xs={4} key={photo.id}>
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={photo.photo_type || "claim photo"}
                          sx={{
                            width: "100%",
                            height: 90,
                            borderRadius: 2,
                            objectFit: "cover",
                            boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                          }}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/150?text=Image";
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No photos uploaded
                </Typography>
              )}
            </AppCard>


            {/* REASON */}
            {claim.reason && (
              <AppCard title="Reason">
                <Typography variant="body2">
                  {claim.reason.title}
                </Typography>
              </AppCard>
            )}

            {/* ACTIONS */}
            <AppCard>
              
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  sx={{ borderRadius: 2 }}
                  onClick={() =>
                    window.open(claim.payment_link, "_blank")
                  }
                >
                  Pay Now
                </Button>
            </AppCard>
          </Stack>
        )}

        {/* =================================================
            TAB 2 – STATUS TIMELINE (APP STYLE)
        ================================================= */}
        {tab === 1 && (
          <AppCard title="Tracking Status">
            <Stack spacing={2} sx={{ mt: 1 }}>
              {CLAIM_STATUS_TEMPLATE.map((step, index) => {
                const isCompleted = index < currentIndex;
                const isActive = index === currentIndex;

                return (
                  <Stack direction="row" key={step.key} spacing={2}>
                    {/* LEFT LINE & DOT */}
                    <Stack alignItems="center">
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          bgcolor: isCompleted
                            ? "#4caf50"
                            : isActive
                            ? "#1976d2"
                            : "#e0e0e0",
                          color: "#fff",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isCompleted ? "✓" : ""}
                      </Box>

                      {index !== CLAIM_STATUS_TEMPLATE.length - 1 && (
                        <Box
                          sx={{
                            width: 2,
                            height: 40,
                            bgcolor:
                              isCompleted || isActive
                                ? "#1976d2"
                                : "#e0e0e0",
                          }}
                        />
                      )}
                    </Stack>

                    {/* RIGHT CONTENT */}
                    <Box>
                      <Typography fontWeight={700}>
                        {step.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {step.description}
                      </Typography>

                      {isActive && (
                        <Typography
                          variant="caption"
                          color="primary"
                        >
                          {formatDateTime(claim.updated_at)}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </AppCard>
        )}
      </Box>
    </Dialog>
  );
}




// import React, { useMemo, useState } from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   Card,
//   CardContent,
//   Chip,
//   Tabs,
//   Tab,
// } from "@mui/material";
// import axios from "axios";
// import toast from "react-hot-toast";

// import {
//   formatDate,
//   formatDateTime,
//   moneyINR,
// } from "../../../utils/helpers";
// import { BASE_URL } from "../../config/api";


// /* --------------------------------------------
//    STATUS FLOW
// --------------------------------------------- */
// const CLAIM_STATUS_TEMPLATE = [
//   { key: "raised", label: "Claim Raised", color: "success" },
//   { key: "otp_sent", label: "OTP Sent", color: "info" },
//   { key: "estimate_sent", label: "Estimate Sent", color: "warning" },
//   { key: "completed", label: "Completed", color: "success" },
//   { key: "rejected", label: "Rejected", color: "error" },
// ];

// export default function ClaimDetailsDialog({
//   open,
//   onClose,
//   claim,
//   device,
//   purchase,
// }) {
//   const [tab, setTab] = useState(0);
//   const [approving, setApproving] = useState(false);

  

//   /* ---------------- PARSE INSPECTION REPORT ---------------- */
//   const inspectionReport = useMemo(() => {
//     try {
//       return JSON.parse(claim.inspection_report || "[]");
//     } catch {
//       return [];
//     }
//   }, [claim]);

//   /* ---------------- APPROVE ESTIMATE ---------------- */
//   const approveEstimate = async () => {
//     try {
//       setApproving(true);
//       await axios.post(
//         `${BASE_URL}warranty/claim/estimate-approve`,
//         { claim_id: claim.id }
//       );
//       toast.success("Estimate approved");
//       onClose(); // close & let parent refresh
//     } catch {
//       toast.error("Failed to approve estimate");
//     } finally {
//       setApproving(false);
//     }
//   };

//   if (!claim) return null;

//   return (
    // <Dialog
    //   open={open}
    //   onClose={onClose}
    //   fullWidth
    //   sx={{
    //     "& .MuiDialog-paper": {
    //       margin: "8px",
    //       width: "calc(100% - 16px)",
    //     },
    //   }}
    // >
//       <Box p={3}>
//         {/* ================= HEADER ================= */}
//         <Stack direction="row" justifyContent="space-between">
//           <Box>
//             <Typography variant="h6" fontWeight={900}>
//               Claim Details
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Claim ID: {claim.id}
//             </Typography>
//           </Box>
//           <Button onClick={onClose}>Close</Button>
//         </Stack>

//         {/* ================= TABS ================= */}
//         <Tabs
//           value={tab}
//           onChange={(_, v) => setTab(v)}
//           sx={{ mt: 2 }}
//         >
//           <Tab label="Claim Details" />
//           <Tab label="Status Timeline" />
//         </Tabs>

//         <Divider sx={{ my: 2 }} />

//         {/* =================================================
//             TAB 1 – CLAIM DETAILS
//         ================================================= */}
//         {tab === 0 && (
//           <Stack spacing={2}>
//             {/* CLAIM SUMMARY */}
//             <Card variant="outlined">
//               <CardContent>
//                 <Typography fontWeight={900}>
//                   {device?.brand} {device?.model}
//                 </Typography>

//                 <Typography variant="body2" color="text.secondary">
//                   Created: {formatDateTime(claim.created_at)}
//                 </Typography>

//                 <Typography
//                   variant="caption"
//                   color="text.secondary"
//                   display="block"
//                   mt={1}
//                 >
//                   Model: {purchase?.model_name || device?.product_name || "—"} •
//                   S/N: {purchase?.serial_no || "—"}
//                   {device?.imei1 ? ` • IMEI: ${device.imei1}` : ""}
//                   {purchase?.warranty_expiry
//                     ? ` • Expiry: ${formatDate(purchase.warranty_expiry)}`
//                     : ""}
//                 </Typography>

//                 <Divider sx={{ my: 1.5 }} />

//                 <Typography variant="body2">
//                   <b>Issues:</b>{" "}
//                   {claim.issue_description || "—"}
//                 </Typography>

//                 {claim.inspection_remark && (
//                   <Typography variant="body2">
//                     <b>Inspection Remark:</b>{" "}
//                     {claim.inspection_remark}
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>

//             {/* PAYMENT SUMMARY */}
//             <Card variant="outlined">
//               <CardContent>
//                 <Typography fontWeight={900}>
//                   Payment Summary
//                 </Typography>

//                 <Divider sx={{ my: 1 }} />

//                 {inspectionReport.map((i, idx) => (
//                   <Stack
//                     key={idx}
//                     direction="row"
//                     justifyContent="space-between"
//                   >
//                     <Typography>{i.label}</Typography>
//                     <Typography>{moneyINR(i.amount)}</Typography>
//                   </Stack>
//                 ))}

//                 <Divider sx={{ my: 1 }} />

//                 <Stack
//                   direction="row"
//                   justifyContent="space-between"
//                   fontWeight={700}
//                 >
//                   <Typography>Total</Typography>
//                   <Typography>
//                     {moneyINR(claim.payable_amount)}
//                   </Typography>
//                 </Stack>
//               </CardContent>
//             </Card>

//             {/* ACTIONS */}
//             <Card variant="outlined">
//               <CardContent>
//                 {claim.estimate_approved ? (
//                   <Button
//                     fullWidth
//                     variant="contained"
//                     onClick={() =>
//                       window.open(claim.payment_link, "_blank")
//                     }
//                   >
//                     Pay Now
//                   </Button>
//                 ) : (
//                   <Stack direction="row" spacing={1}>
//                     <Button
//                       fullWidth
//                       variant="contained"
//                       color="success"
//                       disabled={approving}
//                       onClick={approveEstimate}
//                     >
//                       Approve
//                     </Button>
//                     <Button
//                       fullWidth
//                       variant="contained"
//                       color="error"
//                       onClick={() => toast("Reject flow here")}
//                     >
//                       Reject
//                     </Button>
//                   </Stack>
//                 )}
//               </CardContent>
//             </Card>
//           </Stack>
//         )}

//         {/* =================================================
//             TAB 2 – STATUS TIMELINE
//         ================================================= */}
//         {tab === 1 && (
//           <Card variant="outlined">
//             <CardContent>
//               <Typography fontWeight={900}>
//                 Status Timeline
//               </Typography>

//               <Divider sx={{ my: 1 }} />

//               <Stack spacing={1}>
//                 {CLAIM_STATUS_TEMPLATE.map((st) => {
//                   const hit = claim.status_history?.find(
//                     (h) => h.key === st.key
//                   );

//                   return (
//                     <Stack
//                       key={st.key}
//                       direction="row"
//                       justifyContent="space-between"
//                     >
//                       <Chip
//                         size="small"
//                         label={st.label}
//                         color={hit ? st.color : "default"}
//                         variant={hit ? "filled" : "outlined"}
//                       />
//                       <Typography variant="caption">
//                         {hit?.at
//                           ? formatDateTime(hit.at)
//                           : "Pending"}
//                       </Typography>
//                     </Stack>
//                   );
//                 })}
//               </Stack>
//             </CardContent>
//           </Card>
//         )}
//       </Box>
//     </Dialog>
//   );
// }





// // src/components/dialogs/ClaimDetailsDialog.jsx
// import React from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   Card,
//   CardContent,
//   Chip,
// } from "@mui/material";

// import {
//   formatDate,
//   formatDateTime,
//   moneyINR,
// } from "../../../utils/helpers";

// /* --------------------------------------------
//    STATUS FLOW (same UI, safe mapping)
// --------------------------------------------- */
// const CLAIM_STATUS_TEMPLATE = [
//   { key: "raised", label: "Claim Raised", color: "success" },
//   { key: "otp_sent", label: "OTP Sent", color: "info" },
//   { key: "in_repair", label: "Under Repair", color: "warning" },
//   { key: "completed", label: "Completed", color: "success" },
//   { key: "rejected", label: "Rejected", color: "error" },
// ];

// export default function ClaimDetailsDialog({
//   open,
//   onClose,

//   claim,
//   device,
//   purchase,

//   onPayNow,
//   onRequestPayment,
//   onApprove,
//   onReject,
// }) {
//   if (!claim) return null;

//   return (
//     <Dialog open={open} onClose={onClose} fullWidth 
//     sx={{
//     "& .MuiDialog-paper": {
//       margin: "8px",                 // 👈 reduce outer margin
//       width: "calc(100% - 16px)",    // 👈 prevents overflow
//     },
//   }}>
//       <Box p={3}>
//         {/* ================= HEADER ================= */}
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Box>
//             <Typography variant="h6" fontWeight={900}>
//               Claim Details
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Claim ID: {claim.id}
//             </Typography>
//           </Box>
//           <Button onClick={onClose}>Close</Button>
//         </Stack>

//         <Divider sx={{ my: 2 }} />

//         {/* ================= CLAIM SUMMARY ================= */}
//         <Card variant="outlined">
//           <CardContent>
//             <Typography fontWeight={900}>
//               {device?.brand} {device?.model}
//             </Typography>

//             <Typography variant="body2" color="text.secondary">
//               Created: {formatDateTime(claim.created_at)}
//             </Typography>

//             <Typography
//               variant="caption"
//               color="text.secondary"
//               display="block"
//               mt={1}
//             >
//               Model: {purchase?.model_name || device?.product_name || "—"} •
//               S/N: {purchase?.serial_no || "—"}
//               {device?.imei1 ? ` • IMEI: ${device.imei1}` : ""}
//               {purchase?.warranty_expiry
//                 ? ` • Expiry: ${formatDate(purchase.warranty_expiry)}`
//                 : ""}
//             </Typography>

//             <Divider sx={{ my: 1.5 }} />

//             <Typography variant="body2">
//               <b>Issues:</b>{" "}
//               {claim.issues?.length
//                 ? claim.issues.join(", ")
//                 : claim.issue_description || "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Notes:</b> {claim.notes || "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Photos:</b>{" "}
//               {claim.photos_count
//                 ? `${claim.photos_count} uploaded`
//                 : "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Service:</b>{" "}
//               {claim.claim_type === "pickup"
//                 ? "Pickup"
//                 : "Drop-off"}
//             </Typography>

//             {/* ================= PAYMENT (UNCHANGED UI) ================= */}
//             {claim.payment?.required && (
//               <Card variant="outlined" sx={{ mt: 2 }}>
//                 <CardContent>
//                   <Stack
//                     direction="row"
//                     justifyContent="space-between"
//                     alignItems="center"
//                   >
//                     <Box>
//                       <Typography fontWeight={900}>
//                         Additional Payment
//                       </Typography>
//                       <Typography variant="body2">
//                         Amount:{" "}
//                         <b>{moneyINR(claim.payment.amount)}</b>
//                       </Typography>
//                       <Typography variant="caption" color="text.secondary">
//                         Status: {claim.payment.status}
//                       </Typography>
//                     </Box>

//                     {claim.payment.status !== "paid" ? (
//                       <Button
//                         variant="contained"
//                         onClick={() => onPayNow?.(claim.id)}
//                       >
//                         Pay Now
//                       </Button>
//                     ) : (
//                       <Chip color="success" label="Paid" />
//                     )}
//                   </Stack>
//                 </CardContent>
//               </Card>
//             )}

//             {/* ================= ADMIN ACTIONS (UNCHANGED UI) ================= */}
//             <Card
//               variant="outlined"
//               sx={{ mt: 2, bgcolor: "rgba(0,0,0,0.02)" }}
//             >
//               <CardContent>
//                 <Typography fontWeight={900}>
//                   Admin Actions (demo)
//                 </Typography>

//                 <Stack direction="column" spacing={1} mt={1}>
//                   <Button
//                     variant="outlined"
//                     onClick={() =>
//                       onRequestPayment?.(claim.id, 199)
//                     }
//                   >
//                     Request ₹199
//                   </Button>
//                   <Stack direction="row" spacing={1} mt={1  }>
//                     <Button
//                     variant="contained"
//                     color="success"
//                     onClick={() => onApprove?.(claim.id)}
//                   >
//                     Approve
//                   </Button>
//                   <Button
//                     variant="contained"
//                     color="error"
//                     onClick={() => onReject?.(claim.id)}
//                   >
//                     Reject
//                   </Button>
//                   </Stack>
//                 </Stack>
//               </CardContent>
//             </Card>
//           </CardContent>
//         </Card>

//         {/* ================= STATUS TIMELINE (UNCHANGED UI) ================= */}
//         <Card variant="outlined" sx={{ mt: 2 }}>
//           <CardContent>
//             <Typography fontWeight={900}>
//               Status Timeline
//             </Typography>

//             <Divider sx={{ my: 1 }} />

//             <Stack spacing={1}>
//               {CLAIM_STATUS_TEMPLATE.map((st) => {
//                 const hit = claim.status_history?.find(
//                   (h) => h.key === st.key
//                 );

//                 return (
//                   <Stack
//                     key={st.key}
//                     direction="row"
//                     justifyContent="space-between"
//                     alignItems="center"
//                   >
//                     <Chip
//                       size="small"
//                       label={st.label}
//                       color={hit ? st.color : "default"}
//                       variant={hit ? "filled" : "outlined"}
//                     />
//                     <Typography variant="caption">
//                       {hit?.at
//                         ? formatDateTime(hit.at)
//                         : "Pending"}
//                     </Typography>
//                   </Stack>
//                 );
//               })}
//             </Stack>
//           </CardContent>
//         </Card>
//       </Box>
//     </Dialog>
//   );
// }



// // src/components/dialogs/ClaimDetailsDialog.jsx
// import React from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   Card,
//   CardContent,
//   Chip,
// } from "@mui/material";
// import { formatDate, formatDateTime, moneyINR } from "../../../utils/helpers";


// export default function ClaimDetailsDialog({
//   open,
//   onClose,

//   claim,
//   device,
//   purchase,

//   statusTemplate = [],

//   onPayNow,
//   onRequestPayment,
//   onApprove,
//   onReject,
// }) {
//   if (!claim) return null;

//   return (
//     <Dialog open={open} onClose={onClose} fullWidth>
//       <Box p={3}>
//         {/* HEADER */}
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Box>
//             <Typography variant="h6" fontWeight={900}>
//               Claim Details
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Claim ID: {claim.id}
//             </Typography>
//           </Box>
//           <Button onClick={onClose}>Close</Button>
//         </Stack>

//         <Divider sx={{ my: 2 }} />

//         {/* CLAIM SUMMARY */}
//         <Card variant="outlined">
//           <CardContent>
//             <Typography fontWeight={900}>
//               {device?.name || "Device"}
//             </Typography>

//             <Typography variant="body2" color="text.secondary">
//               Created: {formatDateTime(claim.created_at)}
//             </Typography>

//             <Typography variant="caption" color="text.secondary" display="block" mt={1}>
//               Model: {purchase?.model_name || "—"} •
//               S/N: {purchase?.serial_no || "—"}
//               {purchase?.imei ? ` • IMEI: ${purchase.imei}` : ""}
//               {purchase?.warranty_expiry
//                 ? ` • Expiry: ${formatDate(purchase.warranty_expiry)}`
//                 : ""}
//             </Typography>

//             <Divider sx={{ my: 1.5 }} />

//             <Typography variant="body2">
//               <b>Issues:</b>{" "}
//               {claim.issues?.length ? claim.issues.join(", ") : "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Notes:</b> {claim.notes || "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Photos:</b>{" "}
//               {claim.photos_count ? `${claim.photos_count} uploaded` : "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Service:</b>{" "}
//               {claim.service === "pickup" ? "Pickup" : "Drop-off"}
//             </Typography>

//             {/* PAYMENT */}
//             {claim.payment?.required && (
//               <Card variant="outlined" sx={{ mt: 2 }}>
//                 <CardContent>
//                   <Stack direction="row" justifyContent="space-between" alignItems="center">
//                     <Box>
//                       <Typography fontWeight={900}>Additional Payment</Typography>
//                       <Typography variant="body2">
//                         Amount: <b>{moneyINR(claim.payment.amount)}</b>
//                       </Typography>
//                       <Typography variant="caption" color="text.secondary">
//                         Status: {claim.payment.status}
//                       </Typography>
//                     </Box>

//                     {claim.payment.status !== "paid" ? (
//                       <Button
//                         variant="contained"
//                         onClick={() => onPayNow?.(claim.id)}
//                       >
//                         Pay Now
//                       </Button>
//                     ) : (
//                       <Chip color="success" label="Paid" />
//                     )}
//                   </Stack>
//                 </CardContent>
//               </Card>
//             )}

//             {/* ADMIN (DEMO) */}
//             <Card
//               variant="outlined"
//               sx={{ mt: 2, bgcolor: "rgba(0,0,0,0.02)" }}
//             >
//               <CardContent>
//                 <Typography fontWeight={900}>
//                   Admin Actions (demo)
//                 </Typography>

//                 <Stack direction="row" spacing={1} mt={1}>
//                   <Button
//                     variant="outlined"
//                     onClick={() => onRequestPayment?.(claim.id, 199)}
//                   >
//                     Request ₹199
//                   </Button>
//                   <Button
//                     variant="contained"
//                     color="success"
//                     onClick={() => onApprove?.(claim.id)}
//                   >
//                     Approve
//                   </Button>
//                   <Button
//                     variant="contained"
//                     color="error"
//                     onClick={() => onReject?.(claim.id)}
//                   >
//                     Reject
//                   </Button>
//                 </Stack>
//               </CardContent>
//             </Card>
//           </CardContent>
//         </Card>

//         {/* STATUS TIMELINE */}
//         <Card variant="outlined" sx={{ mt: 2 }}>
//           <CardContent>
//             <Typography fontWeight={900}>Status Timeline</Typography>
//             <Divider sx={{ my: 1 }} />

//             <Stack spacing={1}>
//               {statusTemplate.map((st) => {
//                 const hit = claim.status_history?.find(
//                   (h) => h.key === st.key
//                 );

//                 return (
//                   <Stack
//                     key={st.key}
//                     direction="row"
//                     justifyContent="space-between"
//                     alignItems="center"
//                   >
//                     <Chip
//                       size="small"
//                       label={st.label}
//                       color={hit ? st.color : "default"}
//                       variant={hit ? "filled" : "outlined"}
//                     />
//                     <Typography variant="caption">
//                       {hit?.at ? formatDateTime(hit.at) : "Pending"}
//                     </Typography>
//                   </Stack>
//                 );
//               })}
//             </Stack>
//           </CardContent>
//         </Card>
//       </Box>
//     </Dialog>
//   );
// }


// // src/dialogs/ClaimDetailsDialog.jsx
// import React from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   Card,
//   CardContent,
//   Chip,
// } from "@mui/material";

// import { formatDate, formatDateTime, moneyINR } from "../utils/helpers";

// /**
//  * ClaimDetailsDialog
//  *
//  * Props:
//  * - open (boolean)
//  * - onClose ()
//  * - claim (object)
//  * - device (object)
//  * - purchase (object)
//  * - onPayNow (optional callback)
//  */
// export default function ClaimDetailsDialog({
//   open,
//   onClose,
//   claim,
//   device,
//   purchase,
//   onPayNow,
// }) {
//   if (!claim) return null;

//   const currentStatus =
//     claim.status_history?.[claim.status_history.length - 1];

//   const STATUS_TEMPLATE = [
//     { key: "raised", label: "Claim Raised", color: "success" },
//     { key: "scheduled", label: "Pickup Scheduled", color: "info" },
//     { key: "repair", label: "Under Repair", color: "warning" },
//     { key: "payment_requested", label: "Payment Requested", color: "warning" },
//     { key: "paid", label: "Payment Received", color: "success" },
//     { key: "approved", label: "Approved", color: "success" },
//     { key: "rejected", label: "Rejected", color: "error" },
//     { key: "closed", label: "Closed", color: "default" },
//   ];

//   return (
//     <Dialog open={open} onClose={onClose} fullWidth>
//       <Box p={3}>
//         {/* HEADER */}
//         <Stack
//           direction="row"
//           justifyContent="space-between"
//           alignItems="center"
//         >
//           <Box>
//             <Typography variant="h6" fontWeight={900}>
//               Claim Details
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Claim ID: {claim.id}
//             </Typography>
//           </Box>

//           <Button onClick={onClose}>Close</Button>
//         </Stack>

//         <Divider sx={{ my: 2 }} />

//         {/* CLAIM SUMMARY */}
//         <Card variant="outlined" sx={{ borderRadius: 2 }}>
//           <CardContent>
//             <Typography fontWeight={900}>
//               {device?.name || "Device"}
//             </Typography>

//             <Typography variant="body2" color="text.secondary">
//               Created: {formatDateTime(claim.created_at)}
//             </Typography>

//             <Typography
//               variant="caption"
//               color="text.secondary"
//               sx={{ display: "block", mt: 0.5 }}
//             >
//               Model: {purchase?.model_name || "—"} •
//               S/N: {purchase?.serial_no || "—"}
//               {purchase?.imei &&
//                 ` • IMEI: ${purchase.imei}`}
//               {purchase?.warranty_expiry &&
//                 ` • Expiry: ${formatDate(
//                   purchase.warranty_expiry
//                 )}`}
//             </Typography>

//             <Divider sx={{ my: 1.5 }} />

//             <Typography variant="body2">
//               <b>Issue(s):</b>{" "}
//               {claim.issues?.length
//                 ? claim.issues.join(", ")
//                 : "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Notes:</b>{" "}
//               {claim.notes?.trim() || "—"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Service:</b>{" "}
//               {claim.service === "pickup"
//                 ? "Pickup"
//                 : "Drop-off"}
//             </Typography>

//             <Typography variant="body2">
//               <b>Photos:</b>{" "}
//               {claim.photos_count
//                 ? `${claim.photos_count} uploaded`
//                 : "—"}
//             </Typography>

//             <Stack
//               direction="row"
//               spacing={1}
//               alignItems="center"
//               sx={{ mt: 1 }}
//             >
//               <Chip
//                 size="small"
//                 label={currentStatus?.label || "—"}
//                 color={currentStatus?.color || "default"}
//               />
//             </Stack>
//           </CardContent>
//         </Card>

//         {/* PAYMENT SECTION */}
//         {claim.payment?.required && (
//           <Card
//             variant="outlined"
//             sx={{ borderRadius: 2, mt: 2 }}
//           >
//             <CardContent>
//               <Stack
//                 direction="row"
//                 justifyContent="space-between"
//                 alignItems="center"
//               >
//                 <Box>
//                   <Typography fontWeight={900}>
//                     Additional Payment
//                   </Typography>
//                   <Typography variant="body2">
//                     Amount:{" "}
//                     <b>
//                       {moneyINR(
//                         claim.payment.amount || 0
//                       )}
//                     </b>
//                   </Typography>

//                   <Typography
//                     variant="caption"
//                     color="text.secondary"
//                   >
//                     Status:{" "}
//                     {claim.payment.status === "paid"
//                       ? "Paid"
//                       : "Pending"}
//                     {claim.payment.requested_at &&
//                       ` • Requested: ${formatDateTime(
//                         claim.payment.requested_at
//                       )}`}
//                   </Typography>
//                 </Box>

//                 {claim.payment.status !== "paid" ? (
//                   <Button
//                     variant="contained"
//                     onClick={() =>
//                       onPayNow?.(claim.id)
//                     }
//                   >
//                     Pay Now
//                   </Button>
//                 ) : (
//                   <Chip
//                     color="success"
//                     label="Payment Received"
//                   />
//                 )}
//               </Stack>
//             </CardContent>
//           </Card>
//         )}

//         {/* STATUS TIMELINE */}
//         <Card
//           variant="outlined"
//           sx={{ borderRadius: 2, mt: 2 }}
//         >
//           <CardContent>
//             <Typography fontWeight={900}>
//               Status Timeline
//             </Typography>

//             <Divider sx={{ my: 1.5 }} />

//             <Stack spacing={1}>
//               {STATUS_TEMPLATE.map((st) => {
//                 const hit = claim.status_history?.find(
//                   (h) => h.key === st.key
//                 );

//                 return (
//                   <Stack
//                     key={st.key}
//                     direction="row"
//                     justifyContent="space-between"
//                     alignItems="center"
//                     sx={{
//                       p: 1,
//                       borderRadius: 2,
//                       border:
//                         "1px solid rgba(0,0,0,0.08)",
//                       bgcolor: hit
//                         ? "rgba(79,70,229,0.06)"
//                         : "transparent",
//                     }}
//                   >
//                     <Chip
//                       size="small"
//                       label={st.label}
//                       color={hit ? st.color : "default"}
//                       variant={hit ? "filled" : "outlined"}
//                     />

//                     <Typography
//                       variant="caption"
//                       color="text.secondary"
//                     >
//                       {hit?.at
//                         ? formatDateTime(hit.at)
//                         : "Pending"}
//                     </Typography>
//                   </Stack>
//                 );
//               })}
//             </Stack>
//           </CardContent>
//         </Card>
//       </Box>
//     </Dialog>
//   );
// }
